import { Columns } from "@fullscript/aviary-web";

import { Skeleton } from "@aviary";
import type { Sizes } from "@aviary/components/Columns/grid";
import { CardWrapper } from "@shared/components/ProductCard/CardWrapper/CardWrapper";
import { ContentWrapper } from "@shared/components/ProductCard/ContentWrapper/ContentWrapper";
import { ProductAddToCartWrapper } from "@shared/components/ProductCard/ProductAddToCartWrapper/ProductAddToCartWrapper";
import { useBreakpoints } from "@shared/hooks/useBreakpoints/useBreakpoints";
import { Size } from "@shared/types/sizes";

import * as styles from "./SkeletonProductCard.styles";

interface Props {
  isWide?: boolean;
  gridColumnWidth?: Sizes;
  hasOneButton?: boolean;
}

const SkeletonProductCard = ({ isWide, gridColumnWidth, hasOneButton }: Props) => {
  const { fullHD } = useBreakpoints();

  const columnWidth = () => {
    if (gridColumnWidth) {
      return gridColumnWidth;
    }
    if (fullHD.active) {
      return 4 as Sizes;
    } else {
      return 6 as Sizes;
    }
  };

  const renderButtons = () => {
    if (hasOneButton) {
      return <Skeleton isFullWidth type="button" />;
    }

    return (
      <>
        <Skeleton type="button" />
        <Skeleton type="button" />
      </>
    );
  };

  const renderSkeletonWideCard = () => (
    <Columns.Column columnWidth={12}>
      <CardWrapper isResponsive>
        <ContentWrapper css={styles.wrapperWide} type={Size.LARGE}>
          <Columns>
            <Columns.Column customCss={styles.wideCardImageColumn} columnWidth={3}>
              <Skeleton type="image" imageSize="large" css={styles.skeletonImageWide} />
            </Columns.Column>
            <Columns.Column customCss={styles.productInformationColumn} columnWidth={9}>
              <Skeleton type="typography" typographySize="h2" isFullWidth margin="bottom" />
              <Skeleton type="typography" typographySize="h3" margin="bottom" />
              <Skeleton type="typography" typographySize="h3" width={4} />
              <ProductAddToCartWrapper
                css={styles.productAddToCardWrapper}
                topMarginSize={Size.LARGE}
              >
                {renderButtons()}
              </ProductAddToCartWrapper>
            </Columns.Column>
          </Columns>
        </ContentWrapper>
      </CardWrapper>
    </Columns.Column>
  );

  const renderSkeletonGridCard = () => (
    <Columns.Column columnWidth={columnWidth()}>
      <CardWrapper isResponsive>
        <ContentWrapper type={Size.LARGE}>
          <Skeleton type="image" imageSize="large" css={styles.skeletonImage} />
          <Skeleton type="typography" typographySize="h2" isFullWidth margin="bottom" />
          <Skeleton type="typography" typographySize="h3" margin="bottom" />
          <Skeleton type="typography" typographySize="h3" width={4} />
          <ProductAddToCartWrapper css={styles.productAddToCardWrapper} topMarginSize={Size.LARGE}>
            {renderButtons()}
          </ProductAddToCartWrapper>
        </ContentWrapper>
      </CardWrapper>
    </Columns.Column>
  );

  return <>{isWide ? renderSkeletonWideCard() : renderSkeletonGridCard()}</>;
};

export { SkeletonProductCard };
